/** /settings/prompts — operator-editable prompt registry for the
 *  shared agent loop (W076.5).
 *
 *  feature_prompts is the system-wide prompt + tool-whitelist + per-
 *  call-settings layer. Every call routed through `runAgentLoop` looks
 *  this up first; missing rows fall back to the caller's inline prompt
 *  so call sites migrate one at a time without parity breaks.
 *
 *  This page is intentionally lightweight. The deeper preview surface
 *  is /settings/ai/features (feature_configs). This page owns the
 *  prompt body, the tool whitelist (filtered against the MCP catalog),
 *  and an optional settings JSON. No em dashes.
 */
import { useEffect, useState } from "react";
import { Save, ChevronDown, ChevronRight } from "lucide-react";
import { getCsrfToken } from "../lib/auth";

interface CatalogEntry {
  key: string;
  name: string;
  category: string;
  defaultProvider: string;
  defaultModel: string;
}

interface PromptRow {
  featureId: string;
  promptTemplate: string;
  toolWhitelist: string[];
  settings: Record<string, unknown>;
  version: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ToolDescriptor {
  name: string;
  description: string;
  resource: string;
}

interface DraftState {
  promptTemplate: string;
  toolWhitelist: string[];
  settingsJson: string;
  notes: string;
  dirty: boolean;
}

function blankDraft(): DraftState {
  return { promptTemplate: "", toolWhitelist: [], settingsJson: "{}", notes: "", dirty: false };
}

function rowToDraft(row: PromptRow): DraftState {
  return {
    promptTemplate: row.promptTemplate,
    toolWhitelist: row.toolWhitelist,
    settingsJson: JSON.stringify(row.settings, null, 2),
    notes: row.notes ?? "",
    dirty: false,
  };
}

export function SettingsPrompts() {
  const [prompts, setPrompts] = useState<PromptRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [tools, setTools] = useState<ToolDescriptor[]>([]);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        fetch("/api/settings/prompts", { credentials: "same-origin", cache: "no-store" }),
        fetch("/api/settings/prompts/tools", { credentials: "same-origin", cache: "no-store" }),
      ]);
      if (!pRes.ok) {
        setErr(`/api/settings/prompts returned ${pRes.status}`);
        return;
      }
      const pBody = (await pRes.json()) as { prompts: PromptRow[]; catalog: CatalogEntry[] };
      const tBody = tRes.ok ? ((await tRes.json()) as { tools: ToolDescriptor[] }) : { tools: [] };
      setPrompts(pBody.prompts);
      setCatalog(pBody.catalog);
      setTools(tBody.tools);
      const d: Record<string, DraftState> = {};
      for (const p of pBody.prompts) d[p.featureId] = rowToDraft(p);
      setDrafts(d);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function ensureDraft(featureId: string): DraftState {
    if (drafts[featureId]) return drafts[featureId];
    const fresh = blankDraft();
    setDrafts((d) => ({ ...d, [featureId]: fresh }));
    return fresh;
  }

  function updateDraft(featureId: string, patch: Partial<DraftState>) {
    setDrafts((d) => ({
      ...d,
      [featureId]: { ...(d[featureId] ?? blankDraft()), ...patch, dirty: true },
    }));
  }

  function toggleTool(featureId: string, toolName: string) {
    const cur = ensureDraft(featureId).toolWhitelist;
    const next = cur.includes(toolName) ? cur.filter((t) => t !== toolName) : [...cur, toolName];
    updateDraft(featureId, { toolWhitelist: next });
  }

  async function save(featureId: string) {
    const d = drafts[featureId];
    if (!d) return;
    setSaving(featureId);
    let settings: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(d.settingsJson || "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) settings = parsed;
    } catch {
      setErr(`settings JSON for ${featureId} is not valid JSON.`);
      setSaving(null);
      return;
    }
    try {
      const res = await fetch(`/api/settings/prompts/${encodeURIComponent(featureId)}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() ?? "" },
        body: JSON.stringify({
          promptTemplate: d.promptTemplate,
          toolWhitelist: d.toolWhitelist,
          settings,
          notes: d.notes || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErr((body as { error?: string }).error ?? `save failed (${res.status})`);
        return;
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  const seeded = new Set(prompts.map((p) => p.featureId));
  const unseeded = catalog.filter((c) => !seeded.has(c.key));

  return (
    <div className="mx-auto max-w-4xl px-6 py-8 text-zinc-200">
      <h1 className="mb-1 text-2xl font-semibold">Prompts</h1>
      <p className="mb-6 text-sm text-zinc-400">
        Operator-editable templates for every feature routed through the shared agent loop. A row
        here overrides the inline prompt at the call site. Tool whitelist gates which MCP tools the
        loop may call when multi-turn dispatch ships (W076.22).
      </p>

      {err && (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}
      {loading && <div className="text-sm text-zinc-500">Loading...</div>}

      {!loading && prompts.length === 0 && (
        <div className="mb-6 rounded border border-zinc-800 bg-zinc-900 px-3 py-3 text-sm text-zinc-400">
          No prompt rows yet. Rows seed automatically the first time a migrated call site fires.
          Visit /today, run a recall query, or trigger a brief to seed the first rows.
        </div>
      )}

      <div className="space-y-3">
        {prompts.map((row) => {
          const d = drafts[row.featureId] ?? rowToDraft(row);
          const open = openId === row.featureId;
          const catEntry = catalog.find((c) => c.key === row.featureId);
          return (
            <div key={row.featureId} className="rounded border border-zinc-800 bg-zinc-950">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                onClick={() => setOpenId(open ? null : row.featureId)}
              >
                <div className="flex items-center gap-2">
                  {open ? (
                    <ChevronDown className="h-4 w-4 text-zinc-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-zinc-500" />
                  )}
                  <span className="font-mono text-sm text-zinc-200">{row.featureId}</span>
                  {catEntry && (
                    <span className="text-xs text-zinc-500">{catEntry.name} ({catEntry.category})</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span>v{row.version}</span>
                  <span>updated {row.updatedAt.slice(0, 16).replace("T", " ")}</span>
                  {d.dirty && <span className="text-amber-400">unsaved</span>}
                </div>
              </button>
              {open && (
                <div className="space-y-3 border-t border-zinc-800 px-3 py-3">
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-500">
                      Prompt template
                    </label>
                    <textarea
                      className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 font-mono text-xs text-zinc-200"
                      rows={Math.min(20, Math.max(6, d.promptTemplate.split("\n").length + 1))}
                      value={d.promptTemplate}
                      onChange={(e) => updateDraft(row.featureId, { promptTemplate: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-500">
                      Tool whitelist ({d.toolWhitelist.length} selected)
                    </label>
                    <div className="grid max-h-48 grid-cols-1 gap-1 overflow-y-auto rounded border border-zinc-800 bg-zinc-900 p-2 text-xs sm:grid-cols-2">
                      {tools.map((t) => {
                        const checked = d.toolWhitelist.includes(t.name);
                        return (
                          <label key={t.name} className="flex items-start gap-2 rounded px-1 py-0.5 hover:bg-zinc-800">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={checked}
                              onChange={() => toggleTool(row.featureId, t.name)}
                            />
                            <span className="flex-1">
                              <span className="font-mono text-zinc-200">{t.name}</span>
                              <span className="block text-[10px] text-zinc-500">{t.description.slice(0, 80)}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-500">
                      Settings JSON (overrides per-call)
                    </label>
                    <textarea
                      className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 font-mono text-xs text-zinc-200"
                      rows={5}
                      value={d.settingsJson}
                      onChange={(e) => updateDraft(row.featureId, { settingsJson: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-500">Notes</label>
                    <input
                      type="text"
                      className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200"
                      value={d.notes}
                      onChange={(e) => updateDraft(row.featureId, { notes: e.target.value })}
                    />
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      disabled={!d.dirty || saving === row.featureId}
                      onClick={() => save(row.featureId)}
                      className="inline-flex items-center gap-1 rounded bg-emerald-700 px-3 py-1 text-xs text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500"
                    >
                      <Save className="h-3 w-3" />
                      {saving === row.featureId ? "saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {unseeded.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 text-sm font-semibold text-zinc-300">
            Catalog features not yet routed through the shared loop ({unseeded.length})
          </h2>
          <p className="mb-3 text-xs text-zinc-500">
            These features still use their inline prompt at the call site. They seed into this
            registry the first time the migrated call fires; until then, edits here have no effect.
          </p>
          <ul className="space-y-1 text-xs text-zinc-400">
            {unseeded.map((c) => (
              <li key={c.key} className="flex items-center justify-between border-b border-zinc-800 py-1">
                <span className="font-mono text-zinc-300">{c.key}</span>
                <span>{c.category}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
